/**
 * Winziger Hyperscript-Helper, um ohne Framework typsicher DOM zu bauen.
 *
 *   h("div", { class: "card", onclick: fn }, "Text", childEl)
 *
 * Besonderheiten der Props:
 *   class    -> className
 *   html     -> innerHTML (z. B. für Inline-SVG-Icons)
 *   style    -> Objekt (Partial<CSSStyleDeclaration>) oder String
 *   dataset  -> { key: value } -> data-*
 *   on*      -> addEventListener (onclick, oninput, ...)
 *   sonst    -> Property falls vorhanden, sonst Attribut
 */

export type Child = Node | string | number | null | undefined | boolean;

export interface Props {
  class?: string;
  html?: string;
  style?: Partial<CSSStyleDeclaration> | string;
  dataset?: Record<string, string>;
  [key: string]: unknown;
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props | null = null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);

  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (value == null || value === false) continue;

      if (key === "class") {
        el.className = String(value);
      } else if (key === "html") {
        el.innerHTML = String(value);
      } else if (key === "style") {
        if (typeof value === "string") el.setAttribute("style", value);
        else Object.assign(el.style, value);
      } else if (key === "dataset") {
        Object.assign(el.dataset, value as Record<string, string>);
      } else if (key.startsWith("on") && typeof value === "function") {
        el.addEventListener(
          key.slice(2).toLowerCase(),
          value as EventListener
        );
      } else if (key in el) {
        try {
          (el as Record<string, unknown>)[key] = value;
        } catch {
          el.setAttribute(key, String(value));
        }
      } else {
        el.setAttribute(key, String(value));
      }
    }
  }

  appendChildren(el, children);
  return el;
}

export function appendChildren(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child == null || child === false || child === true) continue;
    if (child instanceof Node) parent.appendChild(child);
    else parent.appendChild(document.createTextNode(String(child)));
  }
}

export function clear(el: Node): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** Leert das Ziel-Element und hängt das neue Kind ein. */
export function mount(target: HTMLElement, child: Node): void {
  clear(target);
  target.appendChild(child);
}

/** Kurz-Helper: <span> mit Inline-SVG (für Icons). */
export function fragment(...children: Child[]): DocumentFragment {
  const frag = document.createDocumentFragment();
  appendChildren(frag, children);
  return frag;
}
